import { PLAElementRecord, PLAProject } from "./PLAState";
import { getPLAExpressionCubeDimensions, validatePLAExpression } from "./PLAExpression";

export type PLAExpressionCompletion = {
   label: string;
   kind: "operation" | "attribute" | "relation" | "dimension" | "member";
   text: string;
   start: number;
   end: number;
   caret: number;
   attributeType?: "constant" | "expression" | "cube";
   valueType?: string;
};

const pipeTemplates = [
   'down("")', 'up("")', 'descendants("")', 'ancestors("")', 'get("")', 'where([attribute] == "")',
   'sum()', 'avg()', 'min()', 'max()', 'count()', 'single()', 'round()', 'reciprocal(0)', 'hasValue()', 'coalesce(0)',
   'slice("", "")', 'select("", "")', 'cell("")', 'sumBy()', 'sumBy("")', 'avgBy("")', 'minBy("")', 'maxBy("")', 'countBy("")', 'toCube("", "", "")', 'toCube("", literal(""), "")',
   'downUntil("", "")', 'upUntil("", "")', 'upWith("", "")', 'getRaw("")', 'relatedValues("", "")',
   'whereAttribute("", "")', 'firstOrZero()', 'singleOrZero()'
];
const scalarTemplates = ['self', 'unitCube("")', 'sum(0)', 'avg(0)', 'min(0)', 'max(0)', 'count(0)', 'round(0)', 'hasValue(0)', 'coalesce(0, 0)', 'firstValue(0, 0)', 'concat("", "")'];

type CallContext = { name: string; argument: number; start: number; argumentStart: number };

export function getPLAExpressionCompletions(expression: string, cursor: number, project: PLAProject, elementTypeId: string, elements: PLAElementRecord[]): PLAExpressionCompletion[] {
   const position = Math.max(0, Math.min(cursor, expression.length));
   const calls: CallContext[] = [];
   let quote: string | undefined;
   let quoteStart = -1;
   const brackets: number[] = [];
   for (let index = 0; index < position; index++) {
      const char = expression[index];
      if (char === "\\") { index++; continue; }
      if (quote) {
         if (char === quote) quote = undefined;
         continue;
      }
      if (char === '"' || char === "'") { quote = char; quoteStart = index; continue; }
      if (char === "[") brackets.push(index);
      if (char === "]") brackets.pop();
      if (brackets.length) continue;
      if (char === "(") calls.push({ name: expression.slice(0, index).match(/([A-Za-z]+)\s*$/)?.[1] || "", argument: 0, start: index, argumentStart: index + 1 });
      if (char === ")") calls.pop();
      if (char === "," && calls.length) {
         const call = calls[calls.length - 1];
         call.argument++;
         call.argumentStart = index + 1;
      }
   }
   const call = calls[calls.length - 1];
   const bracketStart = brackets[brackets.length - 1];
   const word = expression.slice(0, position).match(/[A-Za-z0-9_ÆØÅæøå-]*$/)?.[0] || "";
   let start = quote ? quoteStart + 1 : bracketStart !== undefined ? bracketStart + 1 : position - word.length;
   let end = position;
   if (quote || bracketStart !== undefined) {
      const closing = quote || "]";
      for (; end < expression.length; end++) {
         if (expression[end] === "\\") { end++; continue; }
         if (expression[end] === closing) break;
      }
   } else {
      end += expression.slice(position).match(/^[A-Za-z0-9_ÆØÅæøå-]*/)?.[0].length || 0;
   }
   const prefix = expression.slice(start, position).replace(/\\(.)/g, "$1").toLowerCase();
   const items: { label: string; kind: PLAExpressionCompletion["kind"]; value: string; attributeType?: PLAExpressionCompletion["attributeType"]; valueType?: string }[] = [];
   let expressionPosition = false;
   const addDefinitions = (definitions: Record<string, { name: string; type?: PLAExpressionCompletion["attributeType"]; valueType?: string }>, kind: PLAExpressionCompletion["kind"]) => {
      for (const definition of Object.values(definitions)) {
         if (definition.name) items.push({ label: definition.name, kind, value: definition.name, attributeType: kind === "attribute" ? definition.type : undefined, valueType: kind === "attribute" ? definition.valueType : undefined });
      }
      if (kind === "attribute" && !Object.entries(definitions).some(([id, definition]) => id.toLowerCase() === "name" || definition.name.toLowerCase() === "name")) {
         items.push({ label: "Name", kind, value: "Name", attributeType: "constant", valueType: "string" });
      }
   };
   const addMembers = (typeId: string | undefined) => {
      elements.filter((element) => (!typeId || element.elementType === typeId) && element.name && element.name !== "TOTAL")
         .forEach((element) => items.push({ label: element.name, kind: "member", value: element.name }));
      items.push({ label: "TOTAL", kind: "member", value: "TOTAL" });
   };
   const localAttributes = project.elementTypes[elementTypeId]?.attributes || {};
   const targetAttributes = Object.values(project.elementTypes).reduce<Record<string, { name: string; type?: PLAExpressionCompletion["attributeType"]; valueType?: string }>>((attributes, type) => ({ ...attributes, ...type.attributes }), {});
   const pipelineInput = call ? expression.slice(0, call.start).replace(/[A-Za-z]+\s*$/, "").replace(/\|\s*$/, "").trim() : "";
   const validationContext = { project, elementTypeId };
   const cubeDimensions = pipelineInput && validatePLAExpression(pipelineInput, validationContext).valid ? getPLAExpressionCubeDimensions(pipelineInput, validationContext) : undefined;
   if (bracketStart !== undefined && !quote) {
      const inPredicate = calls.some((entry) => entry.name === "where");
      addDefinitions(inPredicate ? targetAttributes : localAttributes, "attribute");
   } else if (call && (quote || expression.slice(call.argumentStart, position).trim() === word)) {
      if (["down", "up", "descendants", "ancestors", "downUntil", "upUntil", "upWith", "relatedValues"].includes(call.name) && (call.argument === 0 || call.name === "upWith" && call.argument === 1)) {
         addDefinitions(project.relationTypes, "relation");
         items.push(...["outgoing", "incoming"].map((value) => ({ label: value, kind: "relation" as const, value })));
      } else if (["get", "getRaw", "whereAttribute", "downUntil", "upUntil", "relatedValues"].includes(call.name) && call.argument === (["downUntil", "upUntil", "relatedValues"].includes(call.name) ? 1 : 0)) {
         addDefinitions(targetAttributes, "attribute");
      } else if (call.name === "unitCube") {
         addDefinitions(project.elementTypes, "dimension");
      } else if (call.name === "toCube") {
         if (call.argument % 2 === 0) addDefinitions(project.elementTypes, "dimension");
         else addDefinitions(targetAttributes, "attribute");
      } else if (["slice", "select", "sumBy", "avgBy", "minBy", "maxBy", "countBy"].includes(call.name)) {
         if (call.argument === 0 || call.name.endsWith("By")) {
            const dimensionIds = new Set(cubeDimensions || Object.values(project.elementTypes).flatMap((type) => Object.values(type.attributes).flatMap((attribute) => (attribute.cubeDimensions || []).map((dimension) => dimension.elementTypeId))));
            addDefinitions(Object.fromEntries(Array.from(dimensionIds).map((id) => [id, { name: project.elementTypes[id]?.name || id }])), "dimension");
         } else {
            const dimension = expression.slice(call.start + 1, call.argumentStart).match(/^\s*(?:"((?:\\.|[^"])*)"|'((?:\\.|[^'])*)')/);
            const reference = (dimension?.[1] || dimension?.[2] || "").replace(/\\(.)/g, "$1");
            const numericDimension = expression.slice(call.start + 1, call.argumentStart).match(/^\s*(\d+)\s*,/);
            const typeId = numericDimension && cubeDimensions ? cubeDimensions[Number(numericDimension[1])] : Object.entries(project.elementTypes).find(([id, type]) => id === reference || type.name === reference)?.[0];
            addMembers(typeId);
         }
      } else if (call.name === "cell") {
         const typeId = cubeDimensions?.[call.argument];
         addMembers(typeId);
      }
   }
   if (!items.length && !quote && bracketStart === undefined) {
      expressionPosition = true;
      const pipe = /\|\s*$/.test(expression.slice(0, start));
      (pipe ? pipeTemplates : scalarTemplates).forEach((value) => items.push({ label: value, kind: "operation", value }));
      if (!pipe) addDefinitions(calls.some((entry) => entry.name === "where") ? targetAttributes : localAttributes, "attribute");
   }
   const unique = new Set<string>();
   return items.filter((item) => {
      const key = `${item.kind}:${item.value}`;
      if (unique.has(key) || !item.value.toLowerCase().startsWith(prefix) && !item.label.toLowerCase().startsWith(prefix)) return false;
      unique.add(key);
      return true;
   }).map((item) => {
      let text = item.value;
      let caret: number;
      if (quote) {
         text = text.replace(/\\/g, "\\\\").replace(new RegExp(quote, "g"), `\\${quote}`);
         if (expression[end] !== quote) text += quote;
         caret = text.length + (expression[end] === quote ? 1 : 0);
      } else if (bracketStart !== undefined) {
         text = text.replace(/[\\[\]"']/g, "\\$&");
         if (expression[end] !== "]") text += "]";
         caret = text.length + (expression[end] === "]" ? 1 : 0);
      } else if (item.kind !== "operation") {
         text = item.kind === "attribute" && expressionPosition ? `[${text.replace(/[\\[\]"']/g, "\\$&")}]` : `"${text.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
         caret = text.length;
      } else {
         const placeholder = text.indexOf('""');
         caret = placeholder >= 0 ? placeholder + 1 : text.includes("(") && !text.endsWith("()") ? text.indexOf("(") + 1 : text.length;
         if (expression[end] === "(" && text.includes("(")) { text = text.slice(0, text.indexOf("(")); caret = text.length; }
      }
      return { label: item.label, kind: item.kind, text, start, end, caret: start + caret, attributeType: item.attributeType, valueType: item.valueType };
   });
}

export function applyPLAExpressionCompletion(expression: string, completion: PLAExpressionCompletion): { expression: string; cursor: number } {
   return { expression: expression.slice(0, completion.start) + completion.text + expression.slice(completion.end), cursor: completion.caret };
}
